/**
 * Utility functions for user-related operations
 */

import {
  DEFAULT_EXTERNAL_MEMBER_ROLE,
  userTypesForTenantType,
  type TenantType,
  type UserStatus,
  type UserType
} from '@bliks-io/contracts';

/**
 * The userTypes to pre-fill when a member's classification is switched to
 * external: external members must carry an elevated role, so seed the default
 * (sourced from contracts) — but never override a role the admin already chose.
 * Returns undefined when no change is needed.
 */
export function externalDefaultUserTypes(
  nextClassification: string | undefined,
  currentUserTypes: UserType[]
): UserType[] | undefined {
  return nextClassification === 'external' && currentUserTypes.length === 0
    ? [DEFAULT_EXTERNAL_MEMBER_ROLE]
    : undefined;
}

/**
 * Gets a human-readable status label for display in the UI
 * @param status - The user's status
 * @param t - Translation function from useTranslation hook
 * @returns A formatted status label
 */
export function getUserStatusLabel(status?: UserStatus, t?: (key: string) => string): string {
  // If no translation function provided, return the status value as-is (fallback)
  if (!t) {
    return status || 'Unknown';
  }

  switch (status) {
    case 'inactive':
      return t('userStatus.inactive');
    case 'pending':
      return t('userStatus.pending');
    case 'active':
      return t('userStatus.active');
    case 'suspended':
      return t('userStatus.suspended');
    default:
      return status || 'Unknown';
  }
}

/**
 * Gets a visual color scheme for user status badges/indicators
 * @param status - The user's status
 * @returns A colorScheme for Badge component
 */
export function getUserStatusColorScheme(
  status?: UserStatus
): 'success' | 'warning' | 'error' | 'neutral' {
  switch (status) {
    case 'active':
      return 'success'; // green - active users
    case 'pending':
      return 'warning'; // amber - awaiting activation
    case 'suspended':
      return 'error'; // red - blocked account
    case 'inactive':
    default:
      return 'neutral'; // gray - not yet invited
  }
}

/**
 * Gets a human-readable classification label for display in the UI
 * @param classification - The user's classification
 * @param t - Translation function from useTranslation hook
 * @returns A formatted classification label
 */
export function getUserClassificationLabel(
  classification?: string,
  t?: (key: string) => string
): string {
  // If no translation function provided, return the classification value as-is (fallback)
  if (!t) {
    return classification || 'Unknown';
  }

  switch (classification) {
    case 'internal':
      return t('userClassification.internal');
    case 'external':
      return t('userClassification.external');
    default:
      return classification || 'Unknown';
  }
}

/**
 * Shared option type for select/multi-select components and table filters
 * Compatible with MultiSelectItem, DataTable filterOptions, and legacy components
 */
export interface SelectOption {
  label: string;
  value: string;
  description?: string;
}

/**
 * Label and description key per role. Written out rather than templated from
 * the role name so check:translations can see the keys.
 */
const USER_TYPE_LABELS: Record<UserType, { label: string; description: string }> = {
  system_admin: {
    label: 'userTypes.system_admin',
    description: 'userTypes.system_admin_description'
  },
  org_manager: { label: 'userTypes.org_manager', description: 'userTypes.org_manager_description' },
  partner_admin: {
    label: 'userTypes.partner_admin',
    description: 'userTypes.partner_admin_description'
  },
  partner_staff: {
    label: 'userTypes.partner_staff',
    description: 'userTypes.partner_staff_description'
  }
};

/**
 * The role options a member form offers for a tenant of the given type.
 *
 * Which roles exist in which tenant type is a contracts rule — the CASL app
 * split is keyed on tenant type, so offering a customer role for a partner
 * tenant produces a member every partner endpoint refuses.
 *
 * `alsoInclude` re-admits roles a member already holds that the tenant's branch
 * no longer offers. Without it such a role is invisible in the multi-select yet
 * still in the submitted array, so the member could never be repaired: every
 * save would carry the very role the backend refuses.
 *
 * @param t - Translation function from useTranslation hook
 */
export function getUserTypeOptions(
  t: (key: string) => string,
  tenantType: TenantType,
  alsoInclude: readonly UserType[] = []
): SelectOption[] {
  const userTypes = [...new Set([...userTypesForTenantType(tenantType), ...alsoInclude])];
  return userTypes.map((userType) => ({
    label: t(USER_TYPE_LABELS[userType].label),
    value: userType,
    description: t(USER_TYPE_LABELS[userType].description)
  }));
}

/**
 * Gets user status options for forms and filters
 * @param t - Translation function from useTranslation hook
 * @returns Array of user status options with translated labels
 */
export function getUserStatusOptions(t: (key: string) => string): SelectOption[] {
  return [
    { label: t('userStatus.inactive'), value: 'inactive' },
    { label: t('userStatus.pending'), value: 'pending' },
    { label: t('userStatus.active'), value: 'active' },
    { label: t('userStatus.suspended'), value: 'suspended' }
  ];
}

/**
 * Gets user classification options for forms and filters
 * @param t - Translation function from useTranslation hook
 * @returns Array of user classification options with translated labels
 */
export function getUserClassificationOptions(t: (key: string) => string): SelectOption[] {
  return [
    { label: t('userClassification.internal'), value: 'internal' },
    { label: t('userClassification.external'), value: 'external' }
  ];
}

/**
 * Whether an edited email field diverges from the member's stored email.
 * Case-insensitive because the email is the sign-in identifier and the
 * backend normalizes to lowercase — a pure casing edit changes nothing.
 */
export function isEmailEdited(currentEmail: string | undefined, originalEmail: string): boolean {
  return (currentEmail ?? '').trim().toLowerCase() !== originalEmail.trim().toLowerCase();
}
